import { useHistory, useParams } from "react-router";
import {
  PageHeader,
  confirmDialog,
  CapabilityNav,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import { useGetCustomerRole } from "../../../../hooks/useGetCustomerRole";
import VerticalTabs, { TabItem } from "../../../sharedComponents/VerticalTabs";
import { RoleForm } from "./RoleForm";
import { useForm } from "react-hook-form";
import { CustomerRoleFormFields } from "../Utils";
import { useCallback, useEffect, useReducer } from "react";
import useRoleMutations, {
  RoleActions,
  RoleFormFields,
} from "../../../../hooks/useRoleMutations";
import { useQueryClient } from "@tanstack/react-query";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../../context/SnackbarContext";
import { INITIAL_POLICY_STATE, PoliciesReducer } from "./RoleReducer";
import { useFlags } from "launchdarkly-react-client-sdk";
import { navItems } from "../NavHelpers";

const ManageRole = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const allFlags = useFlags();
  const { id } = useParams() as { id: string };
  const [state, dispatch] = useReducer(PoliciesReducer, INITIAL_POLICY_STATE);
  const { role, isFetching, isFetched } = useGetCustomerRole(id);
  const formMethods = useForm<CustomerRoleFormFields>();
  const isReadOnly = !role?.editableByUser;
  const queryClient = useQueryClient();
  const { mutations } = useRoleMutations();
  const { showSnackbar } = useSnackbarActionContext();
  const { roleManagementChangeTracking } = allFlags;

  useEffect(() => {
    formMethods.reset({ name: role?.name, description: role?.description });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isFetched]);

  const handleUpdateRole = useCallback(
    async (
      input: RoleFormFields,
      policies: Array<{ id: string; name: string }>
    ) => {
      if (policies.length < 1) {
        return attemptShowErrorsInSnackbar(showSnackbar, {
          response: { data: { message: t("add_policy_one_policy_message") } },
        });
      }

      await mutations.mutate(RoleActions.UPDATE, input, id, policies);

      formMethods.reset(formMethods.getValues());

      const queries = [
        queryClient.invalidateQueries({ queryKey: ["customerRole"] }),
        queryClient.invalidateQueries({ queryKey: ["policies"] }),
        queryClient.invalidateQueries({ queryKey: ["customerPolicies"] }),
      ];

      return await Promise.all(queries);
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [id]
  );

  const cloneRoleCallback = async () => {
    const formValues = formMethods.getValues();
    const role = await mutations.mutate(RoleActions.CLONE, formValues, id);

    return history.push(`/role-management/all-roles/${role.id}/edit`);
  };

  const handleCloneRole = () => {
    confirmDialog({
      message: t("clone_role_confirm_dialog_message"),
      header: t("universal_clone_role"),
      accept: cloneRoleCallback,
      reject: () => {},
      acceptLabel: t("universal_clone"),
      rejectLabel: t("universal_Cancel"),
    });
  };

  const tabs: TabItem[] = [
    {
      content: (
        <RoleForm
          formMethods={formMethods}
          isLoading={isFetching}
          updateRoleCallback={handleUpdateRole}
          readOnly={isReadOnly}
          roleState={state}
          dispatch={dispatch}
        />
      ),
      title: t("meta_role_details"),
    },
  ];

  return (
    <div className="App">
      <CapabilityNav
        capability="Roles Management"
        navItems={navItems()}
        featureFlags={allFlags}
      />
      <div className="mainContent">
        <PageHeader
          title={role?.name}
          navigationTree={[
            { text: `Accelerate ${t("universal_Home")}`, href: "/" },
            {
              text: t("universal_all_roles"),
              href: "/role-management/all-roles",
            },
            {
              text: t("roles_edit_role_breadcrumb"),
              href: "",
            },
          ]}
          tertiaryButton={{
            label: t("universal_clone"),
            type: "button",
            loading: mutations.isCloning,
            onClick: handleCloneRole,
          }}
          secondaryButton={{
            label: t("universal_Cancel"),
            onClick: () => history.push("/role-management/all-roles"),
          }}
          primaryButton={{
            label: t("universal_Save"),
            type: "submit",
            form: "customer-role-form",
            disabled: isFetching,
            loading: mutations.isUpdating,
            visible: !isReadOnly,
          }}
        />
        <VerticalTabs
          tabs={tabs}
          includeChangeTracking={roleManagementChangeTracking}
          targetIdentifierType="ROLE"
        />
      </div>
    </div>
  );
};

export default ManageRole;
